import React, {useCallback, useEffect, useRef, useState} from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import NavBar from "../components/NavBar.js";
import SettingsMenu from "../components/menus/SettingsMenu";
import UserMenu from "../components/menus/UserMenu";
import InfoMenu from "../components/menus/InfoMenu";
import ChatDownloadMenu from "../components/menus/ChatDownloadMenu";
import HistoryDrawer from "../components/HistoryDrawer";
import {inject, observer} from "mobx-react";
import {toJS} from "mobx";
import {Tooltip} from "react-tooltip";
import {withAuthentication} from "../components/AuthenticateUser";
import {Outlet, useBlocker} from "react-router-dom";
import {ToastContainer} from "react-toastify";
import {useLocation, useNavigate} from "react-router";
import * as Sentry from "@sentry/react";
import { getConfig } from "../lib/configMgr";
import Analytics from "../lib/Analytics";
import { setPageTitle } from "../lib/utils";
import HAL9000Eye from "../components/HAL9000Eye";
import SearchResults from "./SearchResults";
import LandingPage from "./LandingPage";
import { rootStore } from "../stores";
import { searchUUIDFromURL } from "../stores/SearchSessionStore";
import { useTokenRefresh } from "../hooks/useTokenRefresh";
import { useVersionCheck } from "../hooks/useVersionCheck";
import TutorialRunner from "../components/TutorialRunner";

/**
 * NavBarIconLabel
 * Stacks a glyphicon above a small ALL-CAPS text label.
 */
const NavBarIconLabel = ({ icon, label }) => (
  <span className="navbar-icon-label">
    <span className={'glyphicon ' + icon} aria-hidden="true" />
    <span className="navbar-icon-label__text">{label}</span>
  </span>
);

const {
  Sentry: sentryOptions,
  Analytics: analyticsOptions,
  PostHog: postHogOptions,
  defaultToChat = false,
  withAutoplex = false,
} = getConfig();


const App = (props) => {

  const {appStatusStore, userStore, searchSessionStore} = props;
  const location = useLocation();
  const navigate = useNavigate();
  // Marks a navigate() call as app-driven (URL canonicalization/legacy-param rewrite/
  // launching a search from the URL) rather than a user trying to leave — read by the
  // navBlocker effect below, which proceeds through these without the confirm() prompt.
  const skipNavBlockRef = useRef(false);
  const internalNavigate = useCallback((...args) => {
    skipNavBlockRef.current = true;
    navigate(...args);
  }, [navigate]);
  // Collapse repeated leading slashes (e.g. a stray "//" pathname from a malformed
  // external link) before constructing — new URL("//...", origin) parses as
  // protocol-relative and throws "Invalid URL" when there's no host after it.
  // See PLEX-6168/PLEX-6170.
  const normalizedPathname = location.pathname.replace(/^\/+/, "/");
  const url = new URL(`${normalizedPathname}${location.search}${location.hash}`, window.location.origin);
  const isLandingPage = normalizedPathname === "/" || normalizedPathname === "/chat";
  const isIFrame = window.self !== window.top;
  const _now = new Date();
  const foundersDay = _now.getMonth() === 3 && _now.getDate() === 1;

  const {
    awaitingInitialResults,
    queryEntities,
    runNewSearch,
    searchURL,
    fromLocation,
    lastError,
    queuedSearchParams,
    modifySearchParameters,
    clear: clearSearch,
    hasCachedResults,
    restoreCachedResults,
  } = searchSessionStore;

  const forceSubstructure = queryEntities.reduce((result, el) => result || el.id.startsWith("query:"), false);
  const settingsSearchParams = {...queuedSearchParams};
  if (forceSubstructure) {
    settingsSearchParams.simSubstructure = true;
  }
  const {showAdmin, isPowerUser: showPowerUser} = userStore;
  const [showLoading, setShowLoading] = useState(awaitingInitialResults);
  const isPageReload = performance.getEntriesByType("navigation")[0]?.type === "reload";
  const [showChat, setShowChat] = useState(
    withAutoplex && (window.location.hash.indexOf("llm") !== -1 || (defaultToChat && !isPageReload && window.location.hash === ""))
  );

  // React to URL-driven navigation to #llm (e.g. clicking "Welcome" from search results page).
  // useState initializer only runs once on mount, so we need this effect to sync showChat
  // when the hash changes via navigate().
  useEffect(() => {
    if (withAutoplex && isLandingPage && location.hash.indexOf("llm") !== -1 && !showChat) {
      setShowChat(true);
    }
  }, [location.hash, isLandingPage]);
  const [drawerOpen, setDrawerOpen] = useLocalStorage('history-drawer-open', true);
  const [chatState, setChatState] = useState({ conversationActive: false, client: null, showChat: false });
  useVersionCheck(userStore?.isAdmin);

  // Single source of truth for "is there an active chat/search whose state would be lost
  // by navigating away right now". Chat side: an LLM stream in flight or a prompt sent but
  // not yet answered. Search side: hasActiveUncachedSearch mirrors exactly the condition
  // under which clear()'s cacheCurrentResults() call is a no-op (isLoading with searchIDs
  // present) — see SearchSessionStore. Reused below for the HistoryDrawer prop, the
  // in-app navigation guard (useBlocker), the beforeunload guard, and the landing-page
  // mode-toggle buttons, so all of these agree on what counts as "would lose data". See
  // PLEX-6054 gap: completed searches are now cached across navigation, but an in-flight
  // search/chat is not, and previously vanished silently.
  const isSessionActive = showChat
    ? (chatState.client?.streamId != null || chatState.client?.waitingForResponse)
    : searchSessionStore.hasActiveUncachedSearch;

  const ACTIVE_SEARCH_WARNING = "A search is still in progress. Leaving will stop it and you may lose live updates. Continue?";
  const ACTIVE_CHAT_WARNING = "A conversation is still in progress. Leaving will stop it and you may lose live updates. Continue?";
  const confirmLeaveActiveSession = () =>
    window.confirm(showChat ? ACTIVE_CHAT_WARNING : ACTIVE_SEARCH_WARNING);

  // Guard in-app navigation (React Router navigate()/<Link> clicks, and browser
  // back/forward via popstate) while a chat/search is actively streaming. Router-driven
  // navigation only — mode-toggle buttons below (client logo, back-to-search, AI Assist)
  // don't go through the router, so they're guarded separately.
  const navBlocker = useBlocker(isSessionActive);
  useEffect(() => {
    if (navBlocker.state === "blocked") {
      // Internal URL syncs (legacy-param rewrites, canonicalization, launching a search
      // from the URL) fire navigate() themselves and can race isSessionActive flipping
      // true in the same tick — the blocker then intercepts the app's own navigation
      // instead of a user trying to leave. Those calls go through internalNavigate(),
      // which flags skipNavBlockRef so they always proceed silently.
      if (skipNavBlockRef.current) {
        skipNavBlockRef.current = false;
        navBlocker.proceed();
      } else if (confirmLeaveActiveSession()) {
        navBlocker.proceed();
      } else {
        navBlocker.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navBlocker.state]);

  // Guard tab close/refresh while a chat/search is actively streaming. Browsers show their
  // own generic warning here — no custom message or UI is possible.
  useEffect(() => {
    if (!isSessionActive) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isSessionActive]);

  // Extract history_id from URL hash on initial load — passed to ChatSession as historyId prop
  // so it can load the conversation directly on mount.
  const initialHistoryId = (() => {
    if (!showChat) return null;
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return hashParams.get('history_id') || null;
  })();
  const [showOverlay, setShowOverlay] = useState(false);
  const onHistoryLoadStart = useCallback(() => setShowOverlay(true), []);
  const onHistoryLoadEnd = useCallback(() => setShowOverlay(false), []);
  const historyLoadCallbacks = showChat ? { showOverlay, onHistoryLoadStart, onHistoryLoadEnd } : {};

  // Enable proactive token refresh to prevent logout on idle sessions
  useTokenRefresh(userStore);

  useEffect(() => {
    if (withAutoplex) {
      let hash;
      if (showChat) {
        // Preserve history_id in the hash if present — the URL-load effect needs it on page load.
        // Only strip non-llm hashes (e.g. search hashes) when switching to chat.
        const currentHash = window.location.hash;
        hash = currentHash.startsWith('#llm') ? currentHash : '#llm';
      } else {
        hash = "";
      }
      const title = showChat ? "Plex AI Chat" : "Plex Search";
      // Strip ?welcome=true when switching away from chat mode
      let search = window.location.search;
      if (!showChat && search) {
        const params = new URLSearchParams(search);
        params.delete('welcome');
        search = params.toString() ? `?${params.toString()}` : '';
      }
      // Replace current history entry so mode-switching doesn't litter the back stack
      internalNavigate(`${window.location.pathname}${search}${hash}`, { replace: true });
      document.title = title;
    } else if (window.location.hash.indexOf("llm") !== -1) {
      // Redirect /#llm to / when autoplex is disabled
      internalNavigate(`${window.location.pathname}${window.location.search}`, { replace: true });
    }
  }, [showChat]);

  const url2string = (url) => {
    return url ? `${url.pathname}${url.search}${url.hash}` : null;
  };

  useEffect(() => {
    // Replace the initial history entry when opening a new tab (no referrer),
    // so "about:blank" is not left in the back stack. When following a link
    // from another page, preserve that page in the back stack.
    if (!document.referrer) {
      navigate(location, { replace: true });
    }

    if (analyticsOptions?.TRACKING_ID) {
      Analytics.initialize(analyticsOptions?.TRACKING_ID, null, {enable: analyticsOptions?.enable, debug: analyticsOptions?.debug, postHog: postHogOptions});
      // Initial pageview is fired inside initialize(); subsequent route changes
      // are tracked by the location effect below.
    }
    if (sentryOptions.DSN) {
      Sentry.addEventProcessor(event => {
        if (event?.user) {
          event.user.email = userStore.userName;
        }
        return event;
      });
      if (userStore.userName) {
        Sentry.getCurrentScope().setUser({email: userStore.userName});
      }
    }
    return () => {
      Sentry.close();
    }
  }, []);

  useEffect(() => {
    if (analyticsOptions?.TRACKING_ID) {
      Analytics.pageview(location.pathname + location.search + location.hash, document.title);
    }
  }, [location.pathname]);

  useEffect(() => {
    console.debug('[App navigation effect]', {
      isLandingPage,
      searchURL: searchURL?.toString(),
      currentLocation: url2string(location),
      fromHistory: location.state?.fromHistory
    });

    if (isLandingPage) {
      // Clear any in-progress or completed search when navigating to the landing page.
      // Done here (synchronously before render) rather than in LandingPage's useEffect,
      // so search tokens don't flash on the first render and so there's no race where
      // the mount effect fires after a quick forward-navigation cancels the new search.
      clearSearch();
    }
    else {
      // Transparently rewrite legacy search_id query param into history_id hash param.
      // search_id in the URL is a history item UUID — same semantics as history_id.
      const searchQueryParams = new URLSearchParams(location.search);
      if (searchQueryParams.has('search_id')) {
        const legacyId = searchQueryParams.get('search_id');
        searchQueryParams.delete('search_id');
        const newSearch = searchQueryParams.toString() ? `?${searchQueryParams.toString()}` : '';
        const hashParams = new URLSearchParams(location.hash ? location.hash.slice(1) : '');
        if (!hashParams.has('history_id')) {
          hashParams.set('history_id', legacyId);
        }
        internalNavigate(`${location.pathname}${newSearch}#${hashParams.toString()}`, { replace: true });
        return;
      }

      // Helper to launch a search from the current URL location
      const launchSearchFromURL = () => {
        setShowLoading(true);
        searchSessionStore.awaitingInitialResults = true;
        console.debug(`[URL→Search] Launching search from URL ${url2string(location)}`);

        // If a history_id is in the URL, preserve it only when the item has a conversation
        // (turns attached). A plain search item always has a UUID derived from its canonical
        // URL — so if history_id matches the derived UUID, there are no turns.
        const hashParams = location.hash ? new URLSearchParams(location.hash.slice(1)) : null;
        const historyId = hashParams?.get('history_id');
        let conversationId = null;
        let cleanHashStr = "";
        if (historyId) {
          const cleanHash = new URLSearchParams(location.hash.slice(1));
          cleanHash.delete('history_id');
          cleanHashStr = cleanHash.toString() ? `#${cleanHash.toString()}` : '';
        }
        const canonicalUrl = `${location.pathname}${location.search}${cleanHashStr}`;
        const derivedId = searchUUIDFromURL(canonicalUrl);
        if (historyId && historyId !== derivedId) {
          conversationId = historyId;
        }

        // Same search/chat as before this landing-page round trip? Reuse the cached
        // results instead of relaunching — avoids re-running the whole search just to
        // display data we already fetched. Cache key mirrors resultsCacheKey in the store:
        // the history_id if this item has an attached conversation, else the UUID derived
        // from the canonical (history_id-stripped) URL. See PLEX-6054.
        const cacheKey = conversationId || derivedId;
        if (hasCachedResults(cacheKey) && restoreCachedResults(cacheKey)) {
          console.debug(`[URL→Search] Restoring cached results for ${cacheKey} instead of relaunching`);
          setShowLoading(false);
          return;
        }

        try {
          const preResolvedEntities = location.state?.queryEntities || null;
          runNewSearch({location}, {conversationId, queryEntities: preResolvedEntities});
        } catch(e) {
          console.error("runNewSearch failed", e, { location: url2string(location) });
        }
        // Don't navigate - we're already at the correct URL
        // The navigation from landing page already brought us here
        // Navigating again creates duplicate history entries
      };

      if (searchURL === null) {
        const params = fromLocation(location);
        if (!params.searchIDs?.length) {
          console.debug("[No search IDs] Redirect to landing page");
          internalNavigate("/", {replace: true});
        }
        else if (lastError) {
          console.debug("[Search error] Suppressing relaunch due to active error", lastError);
        }
        else {
          launchSearchFromURL();
        }
      }
      // New search from results page, update URL; or make URL canonical
      else if (searchURL.toString() !== url.toString()) {
        if (location.state?.newSearch) {
          // User explicitly submitted a new search (via SearchBox) — launch from the new URL.
          // Clear the newSearch flag immediately (keep queryEntities for launchSearchFromURL to use)
          // so that subsequent effect fires from store updates don't re-launch the search.
          console.debug(`[New search from URL] Launching from ${url}`);
          internalNavigate(url2string(url), { replace: true, state: { queryEntities: toJS(location.state.queryEntities) } });
          launchSearchFromURL();
        } else {
          // Store leads the URL — just make the browser URL canonical (e.g., directConnectionID dropped).
          // Never relaunch from the old URL, as that would re-apply params the store already dropped.
          console.debug(`[Canonical] Update url ${url} => ${searchURL}`);
          internalNavigate(url2string(searchURL), {replace: true});
        }
      }
      else {
        console.debug(`[No change] Already at ${url.toString()}`);
      }
    }
  }, [searchURL, url2string(location), isLandingPage]);

  useEffect(() => {
    if (awaitingInitialResults) {
      setShowLoading(true);
    }
  }, [awaitingInitialResults])

  // Update Sentry context with current search URL for better error reproducibility
  useEffect(() => {
    if (sentryOptions.DSN && searchURL) {
      const fullSearchURL = searchURL.toString();
      Sentry.getCurrentScope().setContext('search', {
        url: fullSearchURL,
        pathname: searchURL.pathname,
        search: searchURL.search,
        hash: searchURL.hash
      });
      Sentry.getCurrentScope().setTag('search_url', fullSearchURL);
    }
  }, [searchURL])

  /* Make admin stuff only visible to admin users (only requires adding the "admin" class) */
  // Add various debug settings here as well
  const adminCSS = `
    .admin {
      ${showAdmin ? "" : "display: none !important;"}
    }
    .power-user {
      ${showAdmin || showPowerUser ? "" : "display: none !important;"}
    }
    .auto-scrolling {
      ${showAdmin ? "border-right: 2px dashed green;" : ""}
    }
    .user-scrolling {
      ${showAdmin ? "border-right: 2px dashed brown;" : ""}
    }
  `;

  // currentId tracks the active history item during the session.
  // Always starts null — no item is pre-selected on page load.
  const [currentId, setCurrentId] = useState(null);

  // Tracks previous showChat value to detect false → true transitions for mode-switch restore.
  const prevShowChatRef = React.useRef(showChat);

  // Update currentId, URL hash, and sessionStorage when conversation becomes active.
  // URL hash (#llm&history_id=<uuid>) enables page-reload restore.
  // sessionStorage (chat-history-id) enables mode-switch restore.
  useEffect(() => {
    if (showChat && chatState?.conversationActive && chatState?.currentConversationId) {
      setCurrentId(chatState.currentConversationId);
      internalNavigate(
        `${window.location.pathname}${window.location.search}#llm&history_id=${chatState.currentConversationId}`,
        { replace: true }
      );
      sessionStorage.setItem('chat-history-id', chatState.currentConversationId);
    }
    // Don't clear when conversationActive becomes false - that happens on page load
    // Only clear via explicit user actions (onNewItem/onClearCurrent handled by those callbacks)
  }, [showChat, chatState?.conversationActive, chatState?.currentConversationId]);

  // Track currentId for search mode based on the store's currentSearchConversationId
  useEffect(() => {
    if (!showChat) {
      setCurrentId(searchSessionStore?.currentSearchConversationId || null);
    }
  }, [showChat, searchSessionStore?.currentSearchConversationId]);

  const { historyStore } = rootStore;

  // Restore conversation from sessionStorage when switching back to chat mode.
  // Only fires on the false → true transition of showChat (mode switch back to chat).
  // Page reload / new tab with history_id is handled by ChatSession's historyId prop.
  // sessionStorage is per-tab, so new tabs always start fresh.
  useEffect(() => {
    const wasChat = prevShowChatRef.current;
    prevShowChatRef.current = showChat;

    // Only act on the false → true transition
    if (!showChat || wasChat) return;
    if (!chatState.onLoadConversation) return;
    if (chatState.conversationActive) return;

    const savedId = sessionStorage.getItem('chat-history-id');
    if (!savedId) return;

    historyStore.getConversation(savedId).then((data) => {
      if (data?.context?.title) setPageTitle(data.context.title);
      chatState.onLoadConversation(data);
    }).catch((err) => {
      console.error('Failed to restore conversation from sessionStorage:', err);
      sessionStorage.removeItem('chat-history-id');
    });
  }, [showChat, chatState.onLoadConversation]);


  return (
    <div className={`app${isLandingPage ? ` landing-page` : ' results-page'}`}>
      <style>{adminCSS}</style>
      {/* History drawer - available in both search and chat modes */}
      {isLandingPage && !isIFrame && (() => {
        return (
        <HistoryDrawer
          mode={showChat ? "conversation" : "search"}
          userStore={userStore}
          currentItemId={currentId}
          isSessionActive={isSessionActive}
          onLoadStart={showChat ? () => setShowOverlay(true) : null}
          onLoadItem={(data) => {
            if (showChat) {
              // Update URL, sessionStorage, and currentId immediately — don't wait for the
              // reactive chain (restoreConversation → chatState → useEffect). That chain
              // requires conversationActive=true and can lag under rapid back-to-back loads,
              // causing the URL to reflect the previous conversation (PLEX-3960).
              if (data?.id) {
                internalNavigate(
                  `${window.location.pathname}${window.location.search}#llm&history_id=${data.id}`,
                  { replace: true }
                );
                sessionStorage.setItem('chat-history-id', data.id);
                setCurrentId(data.id);
              }
              if (data?.context?.title) {
                setPageTitle(data.context.title);
              }
              if (chatState.onLoadConversation) {
                chatState.onLoadConversation(data);
              }
            } else {
              // Load search from history
              if (data?.context?.url) {
                // Set title before navigating so the browser history entry gets the right title
                if (data?.context?.title) {
                  setPageTitle(data.context.title);
                }
                // Parse URL and navigate to it (creates history entry for back button)
                // context.url is stored as a relative path — supply origin as base
                const url = new URL(data.context.url, window.location.origin);
                // Append history item UUID to hash to restore conversation.
                // Strip any pre-existing history_id first — context.url may already
                // contain one from a prior save, which would create a duplicate param.
                const hashParams = new URLSearchParams(url.hash ? url.hash.slice(1) : '');
                hashParams.delete('history_id');
                hashParams.set('history_id', data.id);
                const urlString = `${url.pathname}${url.search}#${hashParams.toString()}`;
                // Navigate with state marker to prevent replace on line 208
                navigate(urlString, { state: { fromHistory: true } });
              }
            }
          }}
          onNewItem={showChat ? () => {
            internalNavigate(
              `${window.location.pathname}${window.location.search}#llm`,
              { replace: true }
            );
            sessionStorage.removeItem('chat-history-id');
            setCurrentId(null);
            if (chatState.onNewConversation) {
              chatState.onNewConversation();
            }
            if (chatState.focusInput) {
              chatState.focusInput();
            }
          } : undefined}
          onClearCurrent={() => {
            if (showChat) {
              internalNavigate(
                `${window.location.pathname}${window.location.search}#llm`,
                { replace: true }
              );
              sessionStorage.removeItem('chat-history-id');
              setCurrentId(null);
              if (chatState.onClearConversation) {
                chatState.onClearConversation();
              }
            } else {
              // Clear saved search ID
              localStorage.removeItem('history-current-search');
              // Clear current search
              searchSessionStore.clear();
            }
          }}
          newItemLabel={showChat ? "New Chat" : "New Search"}
          emptyMessage={showChat ? "No saved conversations" : "No saved searches"}
          emptyHint={showChat ? "Start chatting to create your first conversation" : "Start searching to save your first search"}
        />
      )})()}
      {isLandingPage ? !isIFrame && (<>
        <div className="menu-item-main-page navbar-menus">
          <div className="client-logo" onClick={() => {
            if (isSessionActive && !confirmLeaveActiveSession()) return;
            const wasOpen = drawerOpen;
            setDrawerOpen(false);
            document.body.classList.add('switching-mode');
            setTimeout(() => {
              Analytics.event(Analytics.EVENTS.CHAT_MODE_SWITCH, { mode: showChat ? "search" : "chat" });
              setShowChat(!showChat);
              document.body.classList.remove('switching-mode');
              if (wasOpen) setTimeout(() => setDrawerOpen(true), 100);
            }, 300);
          }}/>
          {showChat && (
            <button
              className="back-to-search-btn"
              onClick={() => {
                if (isSessionActive && !confirmLeaveActiveSession()) return;
                const wasOpen = drawerOpen;
                setDrawerOpen(false);
                document.body.classList.add('switching-mode');
                setTimeout(() => {
                  Analytics.event(Analytics.EVENTS.CHAT_MODE_SWITCH, { mode: "search" });
                  setShowChat(false);
                  searchSessionStore.clear();
                  document.body.classList.remove('switching-mode');
                  if (wasOpen) setTimeout(() => setDrawerOpen(true), 100);
                }, 300);
              }}
              title="Return to search"
            >
              <NavBarIconLabel icon="glyphicon-search" label="Search" />
            </button>
          )}
          {!showChat && (
            <a
              className="back-to-search-btn"
              href="/#llm"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                if (isSessionActive && !confirmLeaveActiveSession()) return;
                const wasOpen = drawerOpen;
                setDrawerOpen(false);
                document.body.classList.add('switching-mode');
                setTimeout(() => {
                  Analytics.event(Analytics.EVENTS.CHAT_MODE_SWITCH, { mode: "chat" });
                  setShowChat(true);
                  document.body.classList.remove('switching-mode');
                  if (wasOpen) setTimeout(() => setDrawerOpen(true), 100);
                }, 300);
              }}
              title={foundersDay ? "What are you doing, Dave?" : "Switch to chat mode"}
            >
              {foundersDay
                ? <HAL9000Eye />
                : <NavBarIconLabel icon="glyphicon-comment" label="AI Assist" />
              }
            </a>
          )}
          {chatState.showChat && chatState.conversationActive && chatState.downloadContext && (
            <ChatDownloadMenu {...chatState.downloadContext} disabled={chatState.downloadContext.helpOnly} />
          )}
          <UserMenu/>
          <SettingsMenu
            appStatusStore={appStatusStore}
            userStore={userStore}
            searchParams={settingsSearchParams}
            modifySearchParameters={modifySearchParameters}
            substructureRequired={forceSubstructure}
          />
          <InfoMenu isLandingPage={isLandingPage} />
        </div>
        <LandingPage
          showChat={showChat}
          toggleShowChat={() => setShowChat(!showChat)}
          onConversationStateChange={setChatState}
          historyId={initialHistoryId}
          {...historyLoadCallbacks}
        />
        </>) : (<>
        <NavBar
          className="app-header navbar-default"
          searchSessionStore={searchSessionStore}
          appStatusStore={appStatusStore}
          userStore={userStore}
          settings={{
            appStatusStore,
            userStore,
            searchParams: settingsSearchParams,
            modifySearchParameters,
            substructureRequired: forceSubstructure,
          }} />
        <SearchResults />
       </>)}
      <Outlet showLoading={awaitingInitialResults || showLoading}/>
      <Tooltip id="tt"/>
      <ToastContainer />
      <TutorialRunner />
    </div>
  );
}

export default withAuthentication(
  inject("searchSessionStore", "appStatusStore", "userStore")(observer(App))
);
